import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import { SceneManager } from '../managers/SceneManager.js'
import { CameraManager } from '../managers/CameraManager.js'
import { InteractionManager } from '../managers/InteractionManager.js'
import { DebugOverlay } from '../components/DebugOverlay.jsx'

export const WebCrafterContext = createContext(null)

/**
 * Provider component for WebCrafter scene state and managers.
 */
export function WebCrafterProvider({ children, isDebugging: initialIsDebugging = false })
{
  /* Loading State */
  const [ sceneManager, setSceneManager ] = useState(null)
  const [ cameraManager, setCameraManager ] = useState(null)
  const [ interactionManager, setInteractionManager ] = useState(null)

  const [ siteData, setSiteData ] = useState(null)
  const [ api, setApi ] = useState(null)

  const [ isLoaded, setIsLoaded ] = useState(false)
  const [ loadingProgress, setLoadingProgress ] = useState(0)
  const [ managersReady, setManagersReady ] = useState(false) // Tracks if managers are initialized

  // Initialize debug state from URL or prop
  const [ isDebugging, setIsDebugging ] = useState(() =>
  {
    if (typeof window !== 'undefined')
    {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get('debug') === 'true' || initialIsDebugging
    }
    return initialIsDebugging
  })


  // Cleanup managers on change or unmount
  React.useEffect(() =>
  {
    return () =>
    {
      if (cameraManager && cameraManager.dispose)
      {
        cameraManager.dispose()
      }
    }
  }, [ cameraManager ])

  const setManagers = useCallback((newSceneManager, newCameraManager, newInteractionManager) =>
  {
    setSceneManager(newSceneManager)
    setCameraManager(newCameraManager)
    setInteractionManager(newInteractionManager)
    if (newSceneManager) setSiteData(newSceneManager.getSiteData())
    setManagersReady(true)
  }, [])

  const initializeManagers = useCallback(({
    scene, controls, animations, actions, mixer, camera,
    setShowPopup, setPopupContent, scrollState = null
  }) =>
  {
    if (sceneManager) return

    const newSceneManager = new SceneManager(scene, controls, animations, actions, mixer)
    const newCameraManager = new CameraManager(newSceneManager, controls, camera, scrollState)
    const newInteractionManager = new InteractionManager(
      setShowPopup, setPopupContent,
      newCameraManager.goToSceneZoneByName,
      newSceneManager.playAnimation,
      newSceneManager.playSound
    )

    setManagers(newSceneManager, newCameraManager, newInteractionManager)

    const siteData = newSceneManager.getSiteData()
    setApi({
      goToSceneZoneByIndex: newCameraManager.goToSceneZoneByIndex,
      goToSceneZoneByName: newCameraManager.goToSceneZoneByName,
      getSceneManager: () => newSceneManager,
      getCameraManager: () => newCameraManager,
      getInteractionManager: () => newInteractionManager,
      getSiteData: () => siteData,
    })

    setManagersReady(true)
  }, [ sceneManager, setManagers ])

  // Router: Map URL paths to Scene Zones
  React.useEffect(() =>
  {
    if (!managersReady || !sceneManager || !cameraManager) return

    const handleRoute = () =>
    {
      let path = window.location.pathname.substring(1)
      if (path.endsWith('/')) path = path.slice(0, -1)
      path = decodeURIComponent(path)

      if (path && path.length > 0)
      {
        const zone = sceneManager.getSceneZone(path)
        if (zone) cameraManager.goToSceneZoneByName(zone.name)
        else cameraManager.goToSceneZoneByIndex(0)
      } else
      {
        cameraManager.goToSceneZoneByIndex(0)
      }
    }

    const t = setTimeout(handleRoute, 100)
    window.addEventListener('popstate', handleRoute)

    return () =>
    {
      window.removeEventListener('popstate', handleRoute)
      clearTimeout(t)
    }
  }, [ managersReady, sceneManager, cameraManager ])

  const disableDebugMode = useCallback(() =>
  {
    setIsDebugging(false)
    if (typeof window !== 'undefined')
    {
      const url = new URL(window.location)
      url.searchParams.delete('debug')
      window.history.replaceState({}, '', url)
    }
  }, [])

  const value = useMemo(() => ({
    sceneManager,
    cameraManager,
    interactionManager,
    siteData,
    isDebugging,
    setManagers,
    initializeManagers,
    api,
    setApi,
    disableDebugMode,
    // Loading state
    isLoaded,
    setIsLoaded,
    loadingProgress,
    setLoadingProgress,
    managersReady
  }), [
    sceneManager,
    cameraManager,
    interactionManager,
    siteData,
    isDebugging,
    setManagers,
    initializeManagers,
    api,
    disableDebugMode,
    // Loading state dependencies
    isLoaded,
    loadingProgress,
    managersReady
  ])

  return (
    <WebCrafterContext.Provider value={value}>
      {children}
      {isDebugging && <DebugOverlay onDisable={disableDebugMode} />}
    </WebCrafterContext.Provider>
  )
}

